// DTO ⇄ view model for the Monitor ▸ Transactions ledger.
import { formatDateOrDash } from '@/lib/format/date';
import { formatContact, formatTextOrDash } from '@/lib/format/text';
import { formatNumber, formatPercentOrDash } from '@/lib/format/number';
import { buildQuantitiesByUnit, defaultUnitType } from '@/lib/mappers/quantities';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { transactionLedgerTypeLabel } from '@/config/statuses';
import { buildTransactionFiltersInput, processFilterOptions } from '@/lib/mappers/filters';

// The server ORs TransactionSearch's keys, so one box searches all three.
const buildTransactionSearchInput = (search) => {
  const term = search?.trim();
  if (!term) return undefined;
  const text = { contains: term, mode: 'insensitive' };
  return {
    sdNo: text,
    material: { name: text },
    supplierCustomer: { companyname: text },
  };
};

// Screen state → `transactions` variables. The list has no sort: the server orders newest first.
export const buildTransactionsQuery = ({
  certificateType,
  page,
  pageSize,
  search,
  type,
  allocationPreference,
  materialId,
  counterpartyAccountId,
  extraFilters,
} = {}) => ({
  certificateType,
  page,
  limit: pageSize,
  search: buildTransactionSearchInput(search),
  filters: buildTransactionFiltersInput(
    { type, allocationPreference, materialId, counterpartyAccountId },
    extraFilters,
  ),
});

export const processTransactionFilterOptions = (result) => ({
  materialOptions: processFilterOptions(result?.materials),
  counterpartyOptions: processFilterOptions(result?.counterparties),
});

const buildUnits = (node) => ({
  [UNIT_TYPES.working]: {
    unit: node.workingUnit,
    amounts: { quantity: node.quantityWorking, balance: node.balanceWorking },
  },
  [UNIT_TYPES.declaration]: {
    unit: node.declarationUnit,
    amounts: { quantity: node.quantityDeclaration, balance: node.balanceDeclaration },
  },
  [UNIT_TYPES.tracking]: {
    unit: node.trackingUnit,
    amounts: { quantity: node.quantityTracking, balance: node.balanceTracking },
  },
});

// Stock adjustments at the site itself — no counterparty; the server's Send to falls back to the site's own name.
const ADJUSTMENT_TYPES = Object.freeze(['SURPLUS', 'WRITE_OFF']);

// The ledger is the one source that splits the name in two.
const buildTransactedBy = (user) =>
  formatContact([user?.firstname, user?.lastname].filter(Boolean).join(' '), user?.email);

export const processTransaction = (node, index, page) => {
  const byUnit = buildQuantitiesByUnit(buildUnits(node));
  return {
    id: node.id,
    // 1-indexed across the whole ledger, so page 2 does not restart at 1.
    serialNo: String(page.offset + index + 1),
    declarationNo: formatTextOrDash(node.sdNo),
    transactionId: formatTextOrDash(node.transactionId),
    associatedId: formatTextOrDash(node.associatedId),
    materialName: formatTextOrDash(node.material?.name),
    associatedSite: formatTextOrDash(node.associatedSite),
    terminal: formatTextOrDash(node.terminal),
    type: transactionLedgerTypeLabel(node.type) ?? formatTextOrDash(node.type),
    // The raw value too: the badge's tone is keyed on the enum, not its label.
    typeCode: node.type ?? null,
    byUnit,
    unitType: defaultUnitType(byUnit),
    sendTo: ADJUSTMENT_TYPES.includes(node.type)
      ? '-'
      : formatTextOrDash(node.supplierCustomer?.companyname),
    transactionDate: formatDateOrDash(node.transactionDate),
    ghgEmission: node.ghgEmission == null ? '-' : formatNumber(node.ghgEmission),
    ghgEmissionSavings: formatPercentOrDash(node.ghgEmissionSavings),
    countryOfOrigin: formatTextOrDash(node.countryOfOrigin),
    rawMaterialType: formatTextOrDash(node.rawMaterialType),
    feedstockCategory: formatTextOrDash(node.feedstockCategory),
    allocationPreference: formatTextOrDash((node.allocationPreference ?? []).join(', ')),
    massBalancePeriod: formatTextOrDash(node.massBalancePeriod?.name),
    transactedBy: buildTransactedBy(node.transactedByUser),
    // Drives the "View attachments" action; the server folds in the inbound batch's files.
    hasAttachments: Boolean(node.hasAttachments),
  };
};

// GET /:module/transactions/:id/attachments — the row's own files and its inbound batch's.
export const processTransactionAttachments = (attachments) =>
  (attachments ?? []).map((attachment, index) => ({
    id: attachment.fileId,
    fileId: attachment.fileId,
    serialNo: index + 1,
    name: formatTextOrDash(attachment.fileName),
    attachmentType: formatTextOrDash(attachment.attachmentType),
  }));

export const processTransactionsPage = (connection) => {
  const pageInfo = connection?.pageInfo;
  const limit = pageInfo?.limit ?? 0;
  const offset = ((pageInfo?.page ?? 1) - 1) * limit;
  return {
    rows: (connection?.nodes ?? []).map((node, index) =>
      processTransaction(node, index, { offset }),
    ),
    totalCount: pageInfo?.total ?? 0,
  };
};
